import { Box, ButtonBase } from '@material-ui/core'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import React, { useCallback } from 'react'
import { Colors } from 'src/components/atoms/Colors'
import { DynamicMuiIcon, MuiIconVariant } from 'src/components/atoms/DynamicMuiIcon'

export type TextAlign = 'left' | 'center' | 'right'

export interface TextAlignSelectorProps {
  value: TextAlign
  onChange: (value: TextAlign) => void
  disabled?: boolean
}

interface AlignOption {
  value: TextAlign
  label: string
  icon: MuiIconVariant
}

const ALIGN_OPTIONS: AlignOption[] = [
  { value: 'left', label: '左揃え', icon: 'formatAlignLeft' },
  { value: 'center', label: '中央', icon: 'formatAlignCenter' },
  { value: 'right', label: '右揃え', icon: 'formatAlignRight' }
]

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      gap: '4px'
    },
    button: {
      flex: 1,
      padding: '8px 4px',
      borderRadius: '3px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '2px',
      minHeight: '44px',
      border: `1px solid ${Colors.background.silver}`,
      backgroundColor: Colors.functional.background.default,
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: Colors.background.lightGray,
        borderColor: Colors.accent.keyPurple.default
      }
    },
    buttonSelected: {
      backgroundColor: Colors.accent.keyPurple.default,
      borderColor: Colors.accent.keyPurple.default,
      color: Colors.functional.background.default,
      '&:hover': {
        backgroundColor: Colors.accent.keyPurple.default,
        borderColor: Colors.accent.keyPurple.default
      }
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    alignLabel: {
      fontSize: '10px',
      marginTop: '2px'
    }
  })
)

export const TextAlignSelector = ({ value, onChange, disabled = false }: TextAlignSelectorProps) => {
  const classes = useStyles()

  const handleClick = useCallback(
    (alignValue: TextAlign) => {
      if (!disabled) {
        onChange(alignValue)
      }
    },
    [onChange, disabled]
  )

  return (
    <Box className={classes.root}>
      {ALIGN_OPTIONS.map(option => {
        const isSelected = value === option.value
        return (
          <ButtonBase
            key={option.value}
            className={clsx(classes.button, isSelected && classes.buttonSelected, disabled && classes.buttonDisabled)}
            onClick={() => handleClick(option.value)}
            disabled={disabled}
            title={option.label}
          >
            <DynamicMuiIcon variant={option.icon} size="20px" color="inherit" />
            <span className={classes.alignLabel}>{option.label}</span>
          </ButtonBase>
        )
      })}
    </Box>
  )
}
